import { marketingHeroSrc } from '@/config/marketing-assets.config'

/** Figma `274:30814` — card grid; modal detail `313:22159` (Alpaca). */
export type BrokerageIntegrationEntry = {
  slug: string
  name: string
  description: string
  /** Long-form modal copy when it differs from the card blurb. */
  detailDescription?: string
  visitUrl?: string
  iconSlug?: string
}

const PLACEHOLDER_DESCRIPTION =
  'Integration details coming soon. This brokerage will be added to the Podium directory as Ivy expands supported connections.'

export function isBrokerageIntegrationPlaceholder(entry: BrokerageIntegrationEntry): boolean {
  return entry.description === PLACEHOLDER_DESCRIPTION
}

export const brokerageIntegrationsCatalog: BrokerageIntegrationEntry[] = [
  {
    slug: 'alpaca',
    name: 'Alpaca',
    iconSlug: 'alpaca-paper',
    description:
      'Commission-free, API-first brokerage built for developers and algorithmic traders. Supports US equities, options, and crypto.',
    detailDescription:
      'Alpaca is an API-first stock brokerage that offers commission-free trading and a developer-friendly platform for building automated trading applications. Headquartered in San Mateo, California, Alpaca supports US equities, options, and cryptocurrency, and has become a popular choice among algorithmic traders and fintech developers building investment products.',
    visitUrl: 'https://alpaca.markets',
  },
  {
    slug: 'chase',
    name: 'Chase',
    description:
      'Self-directed investing from JPMorgan Chase, integrated with your everyday banking. US equities, ETFs, options, and mutual funds.',
    visitUrl: 'https://www.chase.com/personal/investments',
  },
  {
    slug: 'citi',
    name: 'Citi',
    description:
      'Self-directed investing through Citi Self Invest, integrated with your Citi banking relationship. Commission-free US equity trades.',
    visitUrl: 'https://www.citi.com/citi/investor/overview/',
  },
  {
    slug: 'coinbase',
    name: 'Coinbase',
    description:
      'The largest US-based cryptocurrency exchange. Buy, sell, and store BTC, ETH, and hundreds of other digital assets.',
    visitUrl: 'https://www.coinbase.com',
  },
  {
    slug: 'etrade',
    name: 'E*TRADE',
    description:
      'Long-standing online brokerage from Morgan Stanley. Trading for US equities, options, futures, and bonds.',
    visitUrl: 'https://www.etrade.com',
  },
  {
    slug: 'edward-jones',
    name: 'Edward Jones',
    description:
      'Personal advisor-led investing for long-term goals. Portfolios built and managed with a dedicated financial advisor.',
    visitUrl: 'https://www.edwardjones.com',
  },
  {
    slug: 'empower',
    name: 'Empower',
    description:
      'Retirement-focused financial-services firm. Workplace and personal retirement accounts plus self-directed brokerage windows.',
    visitUrl: 'https://www.empower.com',
  },
  {
    slug: 'etoro',
    name: 'eToro',
    description:
      'Multi-asset platform combining brokerage and social trading. Stocks, crypto, and the ability to copy other investors’ portfolios.',
    visitUrl: 'https://www.etoro.com',
  },
  {
    slug: 'fidelity',
    name: 'Fidelity',
    description:
      'Full-service brokerage and asset manager. US equities, options, mutual funds, and bonds with deep research tools.',
    visitUrl: 'https://www.fidelity.com',
  },
  {
    slug: 'interactive-brokers',
    name: 'Interactive Brokers',
    description:
      'Professional-grade trading platform. Global market access across stocks, options, futures, FX, bonds, and funds.',
    visitUrl: 'https://www.interactivebrokers.com',
  },
  {
    slug: 'kraken',
    name: 'Kraken',
    description:
      'US-based cryptocurrency exchange offering spot trading, futures, and staking on hundreds of digital assets.',
    visitUrl: 'https://www.kraken.com',
  },
  {
    slug: 'moomoo',
    name: 'Moomoo',
    iconSlug: 'moomoo',
    description:
      'Global trading platform owned by Futu Holdings. Commission-free US equities and options with advanced charting.',
    visitUrl: 'https://www.moomoo.com',
  },
  // Page 2 placeholders — Figma pagination (`274:30901` shows pages 1–3)
  {
    slug: 'pnc',
    name: 'PNC',
    description: PLACEHOLDER_DESCRIPTION,
    visitUrl: 'https://www.pnc.com/en/personal-banking/investments.html',
  },
  {
    slug: 'robinhood',
    name: 'Robinhood',
    description: PLACEHOLDER_DESCRIPTION,
    visitUrl: 'https://robinhood.com',
  },
  {
    slug: 'schwab',
    name: 'Schwab',
    description: PLACEHOLDER_DESCRIPTION,
    visitUrl: 'https://www.schwab.com',
  },
  {
    slug: 'tastytrade',
    name: 'tastytrade',
    description: PLACEHOLDER_DESCRIPTION,
    visitUrl: 'https://tastytrade.com',
  },
  {
    slug: 'tiaa',
    name: 'TIAA',
    description: PLACEHOLDER_DESCRIPTION,
    visitUrl: 'https://www.tiaa.org',
  },
  {
    slug: 'tradestation',
    name: 'TradeStation',
    description: PLACEHOLDER_DESCRIPTION,
    visitUrl: 'https://www.tradestation.com',
  },
  {
    slug: 'public',
    name: 'Public',
    description: PLACEHOLDER_DESCRIPTION,
    visitUrl: 'https://public.com',
  },
  {
    slug: 'vanguard',
    name: 'Vanguard',
    description: PLACEHOLDER_DESCRIPTION,
    visitUrl: 'https://www.vanguard.com',
  },
  {
    slug: 'webull',
    name: 'Webull',
    description: PLACEHOLDER_DESCRIPTION,
    visitUrl: 'https://www.webull.com',
  },
  {
    slug: 'wells-fargo',
    name: 'Wells Fargo',
    description: PLACEHOLDER_DESCRIPTION,
    visitUrl: 'https://www.wellsfargo.com/investing/',
  },
  {
    slug: 'questrade',
    name: 'Questrade',
    description: PLACEHOLDER_DESCRIPTION,
    visitUrl: 'https://www.questrade.com',
  },
  {
    slug: 'wealthsimple',
    name: 'Wealthsimple',
    description: PLACEHOLDER_DESCRIPTION,
    visitUrl: 'https://www.wealthsimple.com',
  },
  // Page 3 placeholders
  {
    slug: 'placeholder-td-ameritrade',
    name: 'TD Ameritrade',
    description: PLACEHOLDER_DESCRIPTION,
    visitUrl: 'https://www.tdameritrade.com',
  },
  {
    slug: 'placeholder-ally-invest',
    name: 'Ally Invest',
    description: PLACEHOLDER_DESCRIPTION,
    visitUrl: 'https://www.ally.com/invest/',
  },
  {
    slug: 'placeholder-sofi',
    name: 'SoFi Invest',
    description: PLACEHOLDER_DESCRIPTION,
    visitUrl: 'https://www.sofi.com/invest/',
  },
  {
    slug: 'placeholder-merrill',
    name: 'Merrill Edge',
    description: PLACEHOLDER_DESCRIPTION,
    visitUrl: 'https://www.merrilledge.com',
  },
  {
    slug: 'placeholder-usaa',
    name: 'USAA',
    description: PLACEHOLDER_DESCRIPTION,
    visitUrl: 'https://www.usaa.com/inet/wc/investments_main',
  },
  {
    slug: 'placeholder-td-direct',
    name: 'TD Direct Investing',
    description: PLACEHOLDER_DESCRIPTION,
    visitUrl: 'https://www.td.com/ca/en/investing/direct-investing',
  },
  {
    slug: 'placeholder-saxo',
    name: 'Saxo Bank',
    description: PLACEHOLDER_DESCRIPTION,
    visitUrl: 'https://www.home.saxo',
  },
  {
    slug: 'placeholder-degiro',
    name: 'DEGIRO',
    description: PLACEHOLDER_DESCRIPTION,
    visitUrl: 'https://www.degiro.com',
  },
  {
    slug: 'placeholder-trading212',
    name: 'Trading 212',
    description: PLACEHOLDER_DESCRIPTION,
    visitUrl: 'https://www.trading212.com',
  },
  {
    slug: 'placeholder-lightyear',
    name: 'Lightyear',
    description: PLACEHOLDER_DESCRIPTION,
    visitUrl: 'https://lightyear.com',
  },
  {
    slug: 'placeholder-freetrade',
    name: 'Freetrade',
    description: PLACEHOLDER_DESCRIPTION,
    visitUrl: 'https://freetrade.io',
  },
  {
    slug: 'placeholder-stake',
    name: 'Stake',
    description: PLACEHOLDER_DESCRIPTION,
    visitUrl: 'https://hellostake.com',
  },
]

export const brokerageIntegrationsPage = {
  slug: 'brokerage-integrations',
  eyebrow: 'Brokerage Integrations',
  heroTitle: 'Connecting Ivy to the world’s top brokerages',
  heroLead: 'Serving millions of investors.',
  heroImage: marketingHeroSrc('brokerage-integrations'),
  heroImageMaxWidth: 1269, // 1586 × 0.8 (20% smaller)
  directorySearchPlaceholder: 'Search Integrations',
  directoryPageSize: 12,
  directoryModalEyebrow: 'Brokerage Integrations',
  brokerages: brokerageIntegrationsCatalog,
}
